import type { Point } from './connections';

/** Precomputed polyline data for mapping scroll position <-> arc length. */
export interface Geometry {
  points: Point[];
  /** cumulative length at each point; cum[0] === 0 */
  cum: number[];
  total: number;
  startY: number;
  endY: number;
}

export function buildGeometry(points: Point[]): Geometry {
  const cum: number[] = [0];
  for (let i = 1; i < points.length; i++) {
    const a = points[i - 1]!;
    const b = points[i]!;
    cum.push(cum[i - 1]! + Math.hypot(b.x - a.x, b.y - a.y));
  }
  const first = points[0] ?? { x: 0, y: 0 };
  const last = points[points.length - 1] ?? first;
  return { points, cum, total: cum[cum.length - 1] ?? 0, startY: first.y, endY: last.y };
}

/**
 * Arc length along the polyline at which the path reaches document `y`.
 * Assumes the path never travels upward (each segment is vertical-down or
 * horizontal). A horizontal segment counts as fully drawn once `y` reaches it,
 * so the caller should smooth the result if it wants the jog to sweep.
 */
export function arcLengthAtY(g: Geometry, y: number): number {
  const { points, cum } = g;
  if (points.length < 2) return 0;
  if (y <= points[0]!.y) return 0;

  for (let i = 1; i < points.length; i++) {
    const a = points[i - 1]!;
    const b = points[i]!;
    if (y >= b.y) continue; // segment fully behind the focus line
    if (b.y <= a.y) return cum[i - 1]!; // horizontal segment not yet reached
    const t = (y - a.y) / (b.y - a.y);
    return cum[i - 1]! + t * (cum[i]! - cum[i - 1]!);
  }
  return g.total;
}

/** Point on the polyline at arc length `s` (clamped to the path). */
export function pointAt(g: Geometry, s: number): Point {
  const { points, cum } = g;
  if (points.length === 0) return { x: 0, y: 0 };
  if (points.length === 1 || s <= 0) return points[0]!;
  if (s >= g.total) return points[points.length - 1]!;

  for (let i = 1; i < points.length; i++) {
    if (s <= cum[i]!) {
      const a = points[i - 1]!;
      const b = points[i]!;
      const segLen = cum[i]! - cum[i - 1]!;
      const t = segLen === 0 ? 0 : (s - cum[i - 1]!) / segLen;
      return { x: a.x + (b.x - a.x) * t, y: a.y + (b.y - a.y) * t };
    }
  }
  return points[points.length - 1]!;
}

/**
 * Document y the spine tip should sit at for a given scroll position.
 * Normally `scrollY + vh * focus` ("a little below where you're reading"),
 * blended toward the path end so the tip reaches it exactly at max scroll
 * even when there isn't `vh * (1 - focus)` of content below the spine.
 */
export function focusY(
  scrollY: number,
  vh: number,
  maxScroll: number,
  focus: number,
  endY: number
): number {
  const base = scrollY + vh * focus;
  if (maxScroll <= 0) return Math.max(base, endY);
  const reach = maxScroll + vh * focus;
  const extra = Math.max(0, endY - reach);
  const t = Math.min(1, Math.max(0, scrollY / maxScroll));
  return base + extra * t;
}
